import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Sparkles, Compass } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SEEN_KEY = 'nc_reviewer_tour_v1';

const STEPS = [
  {
    title: 'Welcome to the live demo',
    content: "You're inside a real, production-grade NotaryChain environment as a reviewer. Everything here is live, real dashboards, real seals, real blockchain anchors. Explore freely.",
    icon: Sparkles,
  },
  {
    title: 'Your working dashboard',
    content: 'This is a real notary dashboard pre-loaded with ceremonies, document seals and a statutory journal, not static mockups.',
    target: '[data-testid="notary-stats-grid"], [data-testid="navbar-dashboard"]',
  },
  {
    title: 'See the AI notarization flow',
    content: 'The AI demo walks through document remediation, biometric identity and the AI Conductor that guides notaries through statute.',
    target: '[data-testid="navbar-demo"]',
  },
  {
    title: 'Proof lives on Hedera mainnet',
    content: 'Every seal is anchored to a public, decentralized ledger. Open the demo package to seal a sample document live and verify it yourself on HashScan.',
    cta: { label: 'Open the demo package', to: '/investor-demo' },
  },
  {
    title: "That's the tour",
    content: 'Click anything to explore. When you are ready, share what you see, this is the product, running today.',
    icon: Compass,
  },
];

export default function ReviewerTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const isReviewer = !!user?.is_demo_reviewer;
  const onApp = /^\/(dashboard|notary|admin)/.test(location.pathname);

  // Suppress the portal OnboardingTour for the reviewer so only this narrative tour runs.
  useEffect(() => {
    if (!isReviewer) return;
    try {
      ['nc_tour_command_authority_v1', 'nc_tour_assurance_v1', 'nc_tour_client_sovereign_v1']
        .forEach((k) => { if (!localStorage.getItem(k)) localStorage.setItem(k, 'reviewer-suppressed'); });
    } catch { /* ignore */ }
  }, [isReviewer]);

  // Manual replay from the reviewer menu ("Replay tour"), restart regardless of the seen flag.
  useEffect(() => {
    const start = () => { setI(0); setActive(true); };
    window.addEventListener('reviewer:start-tour', start);
    return () => window.removeEventListener('reviewer:start-tour', start);
  }, []);

  useEffect(() => {
    if (!isReviewer || !onApp) return;
    let seen = false;
    try { seen = localStorage.getItem(SEEN_KEY) === '1'; } catch { /* ignore */ }
    if (seen) return;
    const t = setTimeout(() => setActive(true), 900);
    return () => clearTimeout(t);
  }, [isReviewer, onApp]);

  const measure = useCallback(() => {
    const step = STEPS[i];
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [i]);

  useLayoutEffect(() => {
    if (!active) return;
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, measure]);

  if (!active) return null;

  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const Icon = step.icon;

  const close = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
    setActive(false);
  };

  // Card placement: below the target if measured, else centered.
  const cardStyle = rect
    ? {
        top: Math.min(rect.top + rect.height + 14, window.innerHeight - 260),
        left: Math.max(16, Math.min(rect.left, window.innerWidth - 380)),
      }
    : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="reviewer-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-[1px]" onClick={close} />

      {/* Spotlight ring */}
      {rect && (
        <div
          className="absolute rounded-xl ring-4 ring-coral-400 shadow-[0_0_0_9999px_rgba(10,15,25,0.55)] transition-all duration-300 pointer-events-none"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
        />
      )}

      {/* Card */}
      <div
        className="absolute w-[340px] max-w-[calc(100vw-32px)] bg-white rounded-xl shadow-2xl border border-slate-200 p-5"
        style={cardStyle}
        data-testid="reviewer-tour-card"
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
          data-testid="reviewer-tour-close"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          {Icon ? <Icon className="w-5 h-5 text-coral-600" /> : <span className="w-2 h-2 rounded-full bg-coral-500" />}
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-coral-600">Reviewer tour</p>
        </div>
        <h3 className="font-serif text-lg font-bold text-navy-900 mb-1.5">{step.title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{step.content}</p>

        {step.cta && (
          <button
            onClick={() => { close(); navigate(step.cta.to); }}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-coral-600 hover:text-coral-700"
            data-testid="reviewer-tour-cta"
          >
            {step.cta.label} <ChevronRight className="w-4 h-4" />
          </button>
        )}

        <div className="flex items-center justify-between mt-5">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, k) => (
              <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? 'w-5 bg-coral-500' : 'w-1.5 bg-slate-200'}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button
                onClick={() => setI(i - 1)}
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy-900 px-2 py-1"
                data-testid="reviewer-tour-back"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <button
              onClick={() => (last ? close() : setI(i + 1))}
              className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-coral-600 hover:bg-coral-500 rounded-md px-3.5 py-1.5"
              data-testid="reviewer-tour-next"
            >
              {last ? 'Done' : 'Next'} {!last && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {!last && (
          <button onClick={close} className="block w-full text-center text-[11px] text-slate-400 hover:text-slate-600 mt-3" data-testid="reviewer-tour-skip">
            Skip tour
          </button>
        )}
      </div>
    </div>
  );
}
